import React, {
	Fragment,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import update from 'immutability-helper';
import { useNavigate, useParams } from 'react-router-dom';
import {
	EllipsisVerticalIcon,
	PlusIcon,
} from '@heroicons/react/24/outline';
import { ChevronLeftIcon } from '@heroicons/react/20/solid';
import { Menu, Transition } from '@headlessui/react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import moment from 'moment';

import { classNames } from '../../components/helpers/functions';
import { MainContent } from '../../components/Shell/Content';

import useAsync from '../../hooks/useAsync';

import {
	createAdminServiceBlocks,
	deleteAdminServiceBlock,
	getAdminServiceReport,
	getAllAdminServiceBlocks,
	getCustomers,
	updateAdminServiceBlocks,
	updateAdminServiceReport,
	uploadServiceReportImages,
} from '../../api/admin';

import Logo from '../../images/logo-mark.png';

import Modal from '../../components/Shell/Modal';
import {
	TemplateCreateable,
	TemplateDate,
	TemplateEditor,
	TemplateReactSelect,
} from '../../components/Attributes/Attributes_old';
import { Button } from '../../components/Button';
import LoadingSkeleton from '../../components/Shell/LoadingSkeleton';

import useModal from '../../hooks/useModal';
import usePrevious from '../../hooks/usePrevious';

import { Block } from './ServiceReports/Block';

const BLOCK_TYPES = [
	{ label: 'Repair', value: 'repair' },
	{ label: 'Preventive Maintenance', value: 'pm' },
	{ label: 'Notes', value: 'notes' },
	{ label: 'Parts Replaced', value: 'parts' },
];

const ServiceReportsSingle = () => {
	const navigate = useNavigate();
	const { id } = useParams();

	const { open: isNewBlockModalOpen, setOpen: setNewBlockModalOpen } =
		useModal('addContentBlock');

	const [newBlockForm, setNewBlockForm] = useState({});

	const [isAddContentBlockOpen, setAddContentBlockOpen] = useState(false);
	const [isAddPredefinedContentBlockOpen, setAddPredefinedContentBlockOpen] =
		useState(false);

	const [contentBlockForm, setContentBlockForm] = useState({});

	const {
		value: serviceReport,
		loading: isLoadingServiceReport,
		execute: onRefreshServiceReport,
	} = useAsync(getAdminServiceReport, { defaultPayload: { id } });

	const { value: organizations } = useAsync(getCustomers);

	const { execute: onUpdateServiceReport } = useAsync(
		updateAdminServiceReport,
		{ immediate: false },
	);

	const { execute: onCreateServiceBlock } = useAsync(createAdminServiceBlocks, {
		immediate: false,
	});

	const { value: serviceBlocks, execute: onRefreshServiceBlocks } = useAsync(
		getAllAdminServiceBlocks,
		{
			defaultPayload: { predefined: true },
		},
	);

	const { execute: onUpdateServiceBlock } = useAsync(updateAdminServiceBlocks, {
		immediate: false,
	});

	const { execute: onDeleteServiceBlock } = useAsync(deleteAdminServiceBlock, {
		immediate: false,
	});

	const { execute: onUploadImages, loading: isUploadingImages } = useAsync(
		uploadServiceReportImages,
		{ immediate: false },
	);

	const [images, setImages] = useState([]);
	const fileInputRef = useRef(null);

	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

	const [form, setForm] = useState({});
	const [addLineModalOpen, setAddLineModalOpen] = useState(false);
	const [details, setDetails] = useState([]);
	const [line, setLine] = useState({});

	const [cards, setCards] = useState([]);

	const [editingElement, setEditing] = useState(null);

	const prevContentBlockOpen = usePrevious(isAddContentBlockOpen);
	useEffect(() => {
		if (!isAddContentBlockOpen && prevContentBlockOpen) {
			setContentBlockForm({});
			onRefreshServiceReport({ id });
		}
	}, [isAddContentBlockOpen]);

	const prevNewBlockModal = usePrevious(isNewBlockModalOpen);
	useEffect(() => {
		if (!isNewBlockModalOpen && prevNewBlockModal) {
			onRefreshServiceReport({ id });
		}
	}, [isNewBlockModalOpen]);

	useEffect(() => {
		if (serviceReport?.id) {
			if (serviceReport?.blockOrder?.length && serviceReport?.blocks) {
				setCards(
					serviceReport?.blockOrder?.map((blockId) =>
						serviceReport?.blocks?.find((o) => o.id === blockId),
					),
				);
			} else {
				setCards(serviceReport?.blocks || []);
			}
			// Initialize images from service report
			if (serviceReport?.images) {
				setImages(serviceReport.images);
			}
		}
	}, [serviceReport?.id]);

	const handleImageUpload = (event) => {
		const files = event.target.files;
		if (!files || files.length === 0) return;

		onUploadImages({ id, files: Array.from(files) }, (response) => {
			if (response?.files) {
				const newImages = [...images, ...response.files];
				setImages(newImages);
				// Save images to service report
				onUpdateServiceReport(
					{
						id,
						key: 'images',
						value: newImages,
					},
					() => onRefreshServiceReport({ id }),
				);
			}
		});

		// Reset the file input
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleRemoveImage = (imageIndex) => {
		const newImages = images.filter((_, index) => index !== imageIndex);
		setImages(newImages);
		onUpdateServiceReport(
			{
				id,
				key: 'images',
				value: newImages,
			},
			() => onRefreshServiceReport({ id }),
		);
	};

	const handleSavePDF = async () => {
		const element = document.getElementById('invoice-content');
		if (!element) return;

		// Letter size in mm (8.5 x 11 inches)
		const pageWidth = 215.9;
		const pageHeight = 279.4;
		const margin = 15;

		// Target width in pixels for letter-size content (7.5 inches at 96 DPI)
		const targetWidth = 920;

		const canvas = await html2canvas(element, {
			scale: 2,
			useCORS: true,
			allowTaint: true,
			backgroundColor: '#ffffff',
			width: targetWidth,
			onclone: (clonedDoc) => {
				// Set fixed width on cloned element for consistent PDF sizing
				const clonedElement = clonedDoc.getElementById('invoice-content');
				if (clonedElement) {
					clonedElement.style.width = `${targetWidth}px`;
					clonedElement.style.maxWidth = `${targetWidth}px`;
					clonedElement.style.padding = '20px';
					clonedElement.style.boxSizing = 'border-box';
				}

				// Fix oklch/oklab colors (unsupported by html2canvas)
				const resolveColor = (value) => {
					if (
						!value ||
						(!value.includes('oklab') && !value.includes('oklch'))
					) {
						return null;
					}
					const c = document.createElement('canvas');
					c.width = c.height = 1;
					const ctx = c.getContext('2d');
					ctx.fillStyle = value;
					ctx.fillRect(0, 0, 1, 1);
					const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
					return a === 255
						? `rgb(${r},${g},${b})`
						: `rgba(${r},${g},${b},${a / 255})`;
				};

				clonedDoc.querySelectorAll('*').forEach((el) => {
					const styles = getComputedStyle(el);
					['color', 'backgroundColor', 'borderColor'].forEach((prop) => {
						const rgb = resolveColor(styles[prop]);
						if (rgb) el.style[prop] = rgb;
					});
				});
			},
		});

		const imgData = canvas.toDataURL('image/jpeg', 0.95);
		const imgWidth = pageWidth - margin * 2;
		const imgHeight = (canvas.height * imgWidth) / canvas.width;
		const pageContentHeight = pageHeight - margin * 2;

		const pdf = new jsPDF('p', 'mm', 'letter');
		let position = margin;
		let heightLeft = imgHeight;

		pdf.addImage(imgData, 'JPEG', margin, position, imgWidth, imgHeight);
		heightLeft -= pageContentHeight;

		while (heightLeft > 0) {
			position = heightLeft - imgHeight + margin;
			pdf.addPage();
			pdf.addImage(imgData, 'JPEG', margin, position, imgWidth, imgHeight);
			heightLeft -= pageContentHeight;
		}

		const fileName =
			serviceReport?.fileName ||
			`service-report-${serviceReport?.invoiceNumber || 'export'}.pdf`;
		pdf.save(fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`);
	};

	const moveCard = useCallback((dragIndex, hoverIndex) => {
		setCards((prevCards) => {
			const updated = update(prevCards, {
				$splice: [
					[dragIndex, 1],
					[hoverIndex, 0, prevCards[dragIndex]],
				],
			});

			onUpdateServiceReport({
				id,
				key: 'blockOrder',
				value: updated?.map((card) => card?.id),
			});

			return updated;
		});
	}, []);
	const renderCard = useCallback((card, index, serviceReport) => {
		if (!card) {
			return null;
		}
		return (
			<Block
				key={card.id}
				index={index}
				id={card.id}
				block={card}
				text={card.name}
				moveCard={moveCard}
				serviceReport={serviceReport}
				onEdit={(row) => setNewBlockModalOpen({ block: row })}
				onDelete={(row, callback) =>
					onDeleteServiceBlock({ id: row?.id }, () => {
						if (typeof callback === 'function') {
							callback();
						}
						onRefreshServiceReport({ id });
					})
				}
			/>
		);
	}, []);

	return (
		<>
			<MainContent maxWidth="w-full" className="sm:px-0 lg:px-0 py-0 lg:py-0">
				{isLoadingServiceReport === 'pending' ? (
					<LoadingSkeleton />
				) : (
					<>
						<header className="relative isolate">
							<div
								className="absolute inset-0 -z-10 overflow-hidden"
								aria-hidden="true"
							>
								<div className="absolute left-16 top-full -mt-16 transform-gpu opacity-50 blur-3xl xl:left-1/2 xl:-ml-80">
									<div
										className="aspect-[1154/678] w-[72.125rem] bg-gradient-to-br from-[#FF80B5] to-[#9089FC]"
										style={{
											clipPath:
												'polygon(100% 38.5%, 82.6% 100%, 60.2% 37.7%, 52.4% 32.1%, 47.5% 41.8%, 45.2% 65.6%, 27.5% 23.4%, 0.1% 35.3%, 17.9% 0%, 27.7% 23.4%, 76.2% 2.5%, 74.2% 56%, 100% 38.5%)',
										}}
									/>
								</div>
								<div className="absolute inset-x-0 bottom-0 h-px bg-gray-900/5" />
							</div>

							<div className="mx-auto px-4 py-10 sm:px-6 lg:px-8">
								<div className="mx-auto flex max-w-2xl items-center justify-between gap-x-8 lg:mx-0 lg:max-w-none">
									<div className="flex items-center gap-x-6">
										<ChevronLeftIcon
											className="w-5 h-5 text-gray-500 cursor-pointer"
											onClick={() => navigate(-1)}
										/>
										<h1>
											<div className="text-sm leading-6 text-gray-500">
												Invoice{' '}
												<span className="text-gray-700">
													#{serviceReport?.invoiceNumber}
												</span>
											</div>
											<div className="mt-1 text-base font-semibold leading-6 text-gray-900">
												{serviceReport?.organization?.name}
											</div>
										</h1>
									</div>
									<div className="flex items-center gap-x-4 sm:gap-x-6">
										<Button onClick={handleSavePDF}>Download PDF</Button>

										<Menu as="div" className="relative sm:hidden">
											<Menu.Button className="-m-3 block p-3">
												<span className="sr-only">More</span>
												<EllipsisVerticalIcon
													className="h-5 w-5 text-gray-500"
													aria-hidden="true"
												/>
											</Menu.Button>

											<Transition
												as={Fragment}
												enter="transition ease-out duration-100"
												enterFrom="transform opacity-0 scale-95"
												enterTo="transform opacity-100 scale-100"
												leave="transition ease-in duration-75"
												leaveFrom="transform opacity-100 scale-100"
												leaveTo="transform opacity-0 scale-95"
											>
												<Menu.Items className="absolute right-0 z-10 mt-0.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
													<Menu.Item>
														{({ active }) => (
															<button
																type="button"
																className={classNames(
																	active ? 'bg-gray-50' : '',
																	'block w-full px-3 py-1 text-left text-sm leading-6 text-gray-900',
																)}
															>
																Copy URL
															</button>
														)}
													</Menu.Item>
													<Menu.Item>
														{({ active }) => (
															<a
																href="#"
																className={classNames(
																	active ? 'bg-gray-50' : '',
																	'block px-3 py-1 text-sm leading-6 text-gray-900',
																)}
															>
																Edit
															</a>
														)}
													</Menu.Item>
												</Menu.Items>
											</Transition>
										</Menu>
									</div>
								</div>
							</div>
						</header>

						<div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
							<div className="mx-auto grid max-w-2xl grid-cols-1 grid-rows-1 items-start gap-x-8 gap-y-8 lg:mx-0 lg:max-w-none ">
								{/* Invoice */}
								<div
									id="invoice-content"
									className="-mx-4 px-4 py-8 yarn d sm:mx-0 sm:rounded-lg sm:px-8 sm:pb-14 lg:col-span-2 lg:row-span-2 lg:row-end-2 xl:px-16 xl:pb-20 xl:pt-16"
								>
									<div className="-mx-4 sm:mx-0 lg:col-span-2 lg:row-span-2 lg:row-end-2">
										<div className="flex justify-between">
											<div className="flex items-center">
												<img src={Logo} className="h-16 rounded mr-6" />
												<div>
													<h2 className="text-xl font-semibold leading-6 text-gray-900">
														Service Report
													</h2>
													<div className="text-gray-500 text-xs">
														{serviceReport?.invoiceNumber}
													</div>
												</div>
											</div>

											<div className="sm:pr-4 text-sm leading-6 flex items-center">
												<dt className="inline text-gray-500 mr-2">Date:</dt>{' '}
												<dd className="inline text-gray-700">
													<TemplateDate
														isEditing={editingElement === 'date'}
														value={
															editingElement === 'date'
																? moment(serviceReport?.date).toDate()
																: moment(serviceReport?.date).format(
																		'MM/DD/YYYY',
																	)
														}
														onFocus={() => setEditing('date')}
														onBlur={() => setEditing(null)}
														onChange={(index, date) => {
															onUpdateServiceReport(
																{
																	id: serviceReport?.id,
																	key: 'date',
																	value: date,
																},
																() => {
																	setEditing(null);
																	onRefreshServiceReport({ id });
																},
															);
														}}
													/>
												</dd>
											</div>
										</div>
										<dl className="mt-6 grid grid-cols-1 text-sm leading-6 sm:grid-cols-2">
											<div className="mt-6 border-t border-gray-900/5 pt-6 sm:pr-4">
												<dt className="text-gray-600 text-xs">Service by:</dt>
												<dd className="mt-2 text-gray-500">
													<span className="font-medium text-gray-900">
														Cezary Kubel
													</span>
													<br />
													cezary@scipods.com
													<br />
													978-239-8183
												</dd>
												<dd className="mt-2 text-gray-500">
													<span className="font-medium text-gray-900">
														Scipods
													</span>
													<br />
													100 Cummings Center, Suite 218D
													<br />
													Beverly, MA 01915
												</dd>
											</div>
											<div className="mt-6 border-t border-gray-900/5 pt-6 sm:pr-4">
												<dt className="text-gray-600 text-xs">Customer:</dt>
												<dd className="mt-2 text-gray-500">
													<span className="font-medium text-gray-900">
														{serviceReport?.customerName ||
															serviceReport?.organization?.name}
													</span>
													<br />
													<div
														dangerouslySetInnerHTML={{
															__html:
																serviceReport?.customerAddress ||
																serviceReport?.organization?.address ||
																'',
														}}
														style={{ whiteSpace: 'pre-wrap' }}
													/>
													<div data-html2canvas-ignore={true} className="mt-3">
														<TemplateReactSelect
															name="Override Customer"
															placeholder="Select organization..."
															options={organizations?.map((org) => ({
																label: org?.name,
																value: org?.id,
																address: org?.address,
															}))}
															onChange={(index, selected) => {
																if (selected) {
																	onUpdateServiceReport(
																		{
																			id: serviceReport?.id,
																			key: 'customerName',
																			value: selected.label,
																		},
																		() => {
																			if (selected.address) {
																				onUpdateServiceReport(
																					{
																						id: serviceReport?.id,
																						key: 'customerAddress',
																						value: selected.address,
																					},
																					() => onRefreshServiceReport({ id }),
																				);
																			} else {
																				onRefreshServiceReport({ id });
																			}
																		},
																	);
																}
															}}
														/>
													</div>
												</dd>
											</div>
										</dl>

										<table className="mt-16 w-fulltext-left text-sm leading-6">
											<colgroup>
												<col className="w-full" />
												<col />
												<col />
												<col />
											</colgroup>
											<thead className="border-b border-gray-200 text-gray-900">
												<tr>
													<th
														scope="col"
														className="px-0 py-3 font-semibold"
													></th>
													<th
														scope="col"
														className="hidden py-3 pl-8 pr-0 font-semibold sm:table-cell"
													></th>
												</tr>
											</thead>
											<tbody>
												<tr>
													<td className="max-w-0 px-0 py-5 align-top">
														<div className="truncate font-medium text-gray-900">
															{`${serviceReport?.asset?.manufacturer?.name} ${serviceReport?.asset?.model?.name}`}
														</div>
														<div className="truncate text-gray-500">{`S/N: ${serviceReport?.asset?.serialNumber}`}</div>
													</td>

													<td className="hidden py-5 pl-8 pr-0 align-top tabular-nums text-gray-700 sm:table-cell  whitespace-nowrap align-middle	">
														<div className="sm:pr-4 text-sm leading-6 flex items-center">
															<dt className="inline text-gray-500 mr-2">
																Completed on{' '}
															</dt>{' '}
															<dd className="inline text-gray-700 min-w-36">
																<TemplateDate
																	isEditing={editingElement === 'completedOn'}
																	value={
																		editingElement === 'completedOn'
																			? moment(
																					serviceReport?.completedOn,
																				).toDate()
																			: moment(
																					serviceReport?.completedOn,
																				).format('MM/DD/YYYY')
																	}
																	onFocus={() => setEditing('completedOn')}
																	onBlur={() => setEditing(null)}
																	onChange={(index, completedOn) => {
																		onUpdateServiceReport(
																			{
																				id: serviceReport?.id,
																				key: 'completedOn',
																				value: completedOn,
																			},
																			() => {
																				setEditing(null);
																				onRefreshServiceReport({ id });
																			},
																		);
																	}}
																/>
															</dd>
														</div>
													</td>
												</tr>

												<>
													{cards.map((card, i) =>
														renderCard(card, i, serviceReport),
													)}
												</>

												<tr data-html2canvas-ignore={true}>
													<td colSpan={2}>
														<Menu as="div" className="relative">
															<Menu.Button className="block bg-gray-50 py-4 px-6 rounded w-full">
																<div className="flex items-center justify-center cursor-pointer text-xs text-gray-500">
																	<PlusIcon className="w-4 h-4 text-gray-400 mr-3" />{' '}
																	Add Content Block
																</div>
															</Menu.Button>

															<Transition
																as={Fragment}
																enter="transition ease-out duration-100"
																enterFrom="transform opacity-0 scale-95"
																enterTo="transform opacity-100 scale-100"
																leave="transition ease-in duration-75"
																leaveFrom="transform opacity-100 scale-100"
																leaveTo="transform opacity-0 scale-95"
															>
																<Menu.Items className="absolute right-0 z-10 mt-0.5 w-64 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
																	<Menu.Item>
																		{({ active }) => (
																			<button
																				type="button"
																				className={classNames(
																					active ? 'bg-gray-50' : '',
																					'block w-full px-3 py-1 text-left text-sm leading-6 text-gray-900',
																				)}
																				onClick={() =>
																					setAddContentBlockOpen(true)
																				}
																			>
																				New Block
																			</button>
																		)}
																	</Menu.Item>
																	<Menu.Item>
																		{({ active }) => (
																			<a
																				className={classNames(
																					active ? 'bg-gray-50' : '',
																					'block px-3 py-1 text-sm leading-6 text-gray-900',
																				)}
																				onClick={() =>
																					setAddPredefinedContentBlockOpen(true)
																				}
																			>
																				Predefined Blocks
																			</a>
																		)}
																	</Menu.Item>
																</Menu.Items>
															</Transition>
														</Menu>
													</td>
												</tr>

												{details?.map((item) => (
													<>
														<tr>
															<td className="max-w-0 px-0 py-5 align-top">
																<div className="truncate font-medium text-gray-900">
																	{item?.name}
																</div>
																<div className="truncate text-gray-500">{`S/N: ${item?.serialNumber}`}</div>
															</td>

															<td className="hidden py-5 pl-8 pr-0 align-top tabular-nums text-gray-700 sm:table-cell  whitespace-nowrap align-middle	">
																<div className="flex items-center">
																	Completed on{' '}
																	{moment(item?.completedDate).format(
																		'MMM Do, YYYY',
																	)}
																</div>
															</td>
														</tr>
														{item?.type === 'repair' && (
															<>
																<tr>
																	<td colSpan={2}>
																		<div className="bg-red-50 py-4 px-6 rounded-md mb-4">
																			<div class="font-medium text-gray-900">
																				Repair
																			</div>
																			<div class="text-xs text-gray-500">
																				<span className="font-semibold">
																					Reported Issue:
																				</span>{' '}
																				{item?.reportedIssue}
																			</div>

																			<div className="mt-4 px-4 text-xs leading-6">
																				<div className="flex items-center">
																					{item?.fix}
																				</div>
																			</div>
																		</div>
																	</td>
																</tr>
															</>
														)}
														{item?.type === 'pm' && (
															<>
																<tr>
																	<td colSpan={2}>
																		<div className="bg-indigo-50 py-4 px-6 rounded-md mb-4">
																			<div class="font-medium text-gray-900">
																				Preventive Maintenance
																			</div>
																			<div class="text-xs text-gray-500">
																				<span className="font-semibold">
																					Reported Issue:
																				</span>{' '}
																				{item?.reportedIssue}
																			</div>

																			<div className="mt-4 px-4 text-xs leading-6">
																				<div className="flex items-center">
																					{item?.fix}
																				</div>
																			</div>
																		</div>
																	</td>
																</tr>
															</>
														)}
													</>
												))}
											</tbody>
										</table>

										<table className="mt-16 w-fulltext-left text-sm leading-6">
											<colgroup>
												<col className="w-full" />
												<col />
												<col />
												<col />
											</colgroup>
											<thead className="border-b border-gray-200 text-gray-900">
												<tr>
													<th
														scope="col"
														className="px-0 py-3 font-semibold"
													></th>
													<th
														scope="col"
														className="hidden py-3 pl-8 pr-0 font-semibold sm:table-cell"
													></th>
												</tr>
											</thead>
											<tbody>
											</tbody>
										</table>

										<div className="flex justify-center items-center text-center mt-10">
											<div>
												<div className="text-gray-700 font-semibold text-xs">
													Generated by Scipods
												</div>

												<img
													src="https://cloud.scipods.com/images/1e77163140de92f0cde1d3d1b99ba082.png"
													className="h-10 w-10 m-auto my-4"
												/>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</>
				)}

				<Modal
					title="Add block"
					open={isAddContentBlockOpen}
					onClose={() => setAddContentBlockOpen(false)}
				>
					<div className="grid grid-cols-2 gap-y-4">
						<TemplateCreateable
							name="Type"
							options={BLOCK_TYPES}
							onChange={(index, type, raw) => {
								setContentBlockForm({ ...contentBlockForm, type: raw });
							}}
							value={contentBlockForm?.type?.value}
						/>

						{contentBlockForm?.type?.value === 'repair' && (
							<>
								<TemplateEditor
									name="Reported Issue"
									value={contentBlockForm?.issue?.delta}
									onChange={(index, issue, raw) =>
										setContentBlockForm({ ...contentBlockForm, issue: raw })
									}
									quillClassName="h-36 mb-2"
									toolbar={false}
								/>
								<TemplateEditor
									name="Solution"
									value={contentBlockForm?.solution?.delta}
									onChange={(index, solution, raw) =>
										setContentBlockForm({ ...contentBlockForm, solution: raw })
									}
									quillClassName="h-36 mb-2"
									toolbar={false}
								/>
							</>
						)}

						{contentBlockForm?.type?.value === 'pm' && (
							<>
								<TemplateEditor
									name="Content"
									value={contentBlockForm?.pm?.delta}
									onChange={(index, solution, raw) =>
										setContentBlockForm({ ...contentBlockForm, pm: raw })
									}
									quillClassName="h-36 mb-2"
									toolbar={false}
								/>
							</>
						)}

						{contentBlockForm?.type?.value === 'notes' && (
							<>
								<TemplateEditor
									name="Content"
									value={contentBlockForm?.notes?.delta}
									onChange={(index, solution, raw) =>
										setContentBlockForm({ ...contentBlockForm, notes: raw })
									}
									quillClassName="h-36 mb-2"
									toolbar={false}
								/>
							</>
						)}

						{contentBlockForm?.type?.value === 'parts' && (
							<>
								<TemplateEditor
									name="Content"
									value={contentBlockForm?.parts?.delta}
									onChange={(index, solution, raw) =>
										setContentBlockForm({ ...contentBlockForm, parts: raw })
									}
									quillClassName="h-36 mb-2"
									toolbar={false}
								/>
							</>
						)}

						<div className="flex justify-end col-span-2">
							<Button
								onClick={() =>
									onCreateServiceBlock(
										{
											serviceReportId: serviceReport?.id,
											blocks: [
												...(contentBlockForm?.issue
													? [
															{
																name: 'Reported Issue',
																content: contentBlockForm?.issue?.content,
																delta: contentBlockForm?.issue?.delta,
																type: 'repair',
															},
														]
													: []),
												...(contentBlockForm?.solution
													? [
															{
																name: 'Solution',
																content: contentBlockForm?.solution?.content,
																delta: contentBlockForm?.solution?.delta,
																type: 'solution',
															},
														]
													: []),
												...(contentBlockForm?.pm
													? [
															{
																name: 'Preventive Maintenance',
																content: contentBlockForm?.pm?.content,
																delta: contentBlockForm?.pm?.delta,
																type: 'pm',
															},
														]
													: []),
												...(contentBlockForm?.notes
													? [
															{
																name: 'Notes',
																content: contentBlockForm?.notes?.content,
																delta: contentBlockForm?.notes?.delta,
																type: 'notes',
															},
														]
													: []),
												...(contentBlockForm?.parts
													? [
															{
																name: 'Parts Replaced',
																content: contentBlockForm?.parts?.content,
																delta: contentBlockForm?.parts?.delta,
																type: 'parts',
															},
														]
													: []),
											],
										},
										() => setAddContentBlockOpen(false),
									)
								}
							>
								Add Block(s)
							</Button>
						</div>
					</div>
				</Modal>
				<Modal
					title="Add a predefined content block"
					open={isAddPredefinedContentBlockOpen}
					onClose={() => setAddPredefinedContentBlockOpen(false)}
				>
					<div className="grid grid-cols-2 gap-y-4">
						<TemplateReactSelect
							name="Block"
							options={serviceBlocks?.map((block) => ({
								label: block?.name,
								value: block?.id,
								block,
							}))}
							onChange={(index, selectedBlock) => {
								setAddPredefinedContentBlockOpen(false);
								setAddContentBlockOpen({
									id: selectedBlock?.value,
									serviceReportId: serviceReport?.id,
								});
								setContentBlockForm({
									...contentBlockForm,
									type: { value: selectedBlock?.block?.type },
									pm: {
										content: selectedBlock?.block?.content,
										delta: selectedBlock?.block?.delta,
									},
								});
							}}
						/>
					</div>{' '}
				</Modal>
			</MainContent>
		</>
	);
};

export default ServiceReportsSingle;
